<template>
    <div class="mnu-app">
        <!-- Vérification d'accès. Rien de la carte n'est monté tant que le
             serveur (ou le cache) n'a pas validé la clé du QR. -->
        <div v-if="bootState === 'checking'" class="mnu-boot">
            <div class="mnu-boot-spin"></div>
            <p class="mnu-boot-txt">Ouverture de la carte…</p>
            <p v-if="seedProgress" class="mnu-boot-sub">
                Téléchargement {{ seedProgress.fait }} / {{ seedProgress.total }}
            </p>
        </div>

        <!-- 'denied' n'affiche rien : la redirection vers la page de refus est
             déjà lancée, et monter la carte le temps d'un tick la ferait
             clignoter. -->
        <template v-else-if="bootState !== 'denied'">
        <div v-if="bootState === 'offline'" class="mnu-offline">
            <i class="ri-wifi-off-line"></i>
            <span>Hors ligne - carte enregistrée sur cet appareil.</span>
        </div>

        <lacarte
            v-if="view === 'menu'"
            :restaurant-nom="restaurantNom"
            :table-label="tableLabel"
            :loading="loading"
            :erreur="erreur"
            :items="items"
            :sections="sections"
            :categories="categories"
            :active-cat="activeCat"
            :tab="tab"
            :has-day-menu="hasDayMenu"
            :date-du-jour="dateDuJour"
            :search="search"
            :qty-map="qtyParPid"
            :cart-count="cartCount"
            :cart-total="cartTotal"
            @update:tab="tab = $event"
            @update:search="search = $event"
            @aller-categorie="allerCategorie"
            @ouvrir-plat="ouvrirPlat"
            @voir-commande="view = 'cart'"
            @recharger="loadMenu"
        />

        <macommande
            v-else-if="view === 'cart'"
            :cart="cart"
            :total="cartTotal"
            :table-label="tableLabel"
            :client-nom="clientNom"
            :submitting="submitting"
            @retour="retourCarte"
            @supprimer="supprimerLigne"
            @quantite="changerQuantite"
            @envoyer="passerCommande"
            @update:clientNom="clientNom = $event"
        />

        <commandeenvoyee
            v-else-if="view === 'confirm'"
            :order-ref="orderRef"
            :last-order="lastOrder"
            :table-label="tableLabel"
            :restaurant-nom="restaurantNom"
            @nouvelle-commande="retourCarte"
        />

        <ficheplat
            v-if="platOuvert"
            :item="platOuvert"
            @fermer="platOuvert = null"
            @confirmer="ajouterAuPanier"
        />

        <!-- Le pied de page porte aussi la garde au-dessus de la barre panier flottante. -->
        <footer v-if="view === 'menu'" class="mnu-footer">Proposé par HSIGCOM</footer>
        </template>
    </div>
</template>

<script>
    /* eslint-disable */

    // La carte est servie par le catalogue Scan & Go : le menu n'a pas
    // d'endpoint à lui, il consomme /verifyaccess comme le panier. Même
    // machine à états de démarrage, même cache IndexedDB, même scope
    // (soc|mag) - un seul téléchargement pour les deux modules.
    import {
        getOrCreateDeviceId,
        getSession, isSessionFresh, saveSession, clearSession,
        getCatalogueMeta, touchCatalogueMeta, saveCatalogue,
        applyCatalogueDelta, verifyCatalogueIntegrity, clearCatalogue,
        getAllArticles, verifierAccesScanGo,
    } from "@/service/hsigscangoIndexdb";
    import { catalogueVersCarte } from "./mapping";

    import lacarte from "./lacarte.vue";
    import ficheplat from "./ficheplat.vue";
    import macommande from "./macommande.vue";
    import commandeenvoyee from "./commandeenvoyee.vue";
    import { slug, sansAccent, platDuJour } from "./format";


    let _uid = 0;

    export default {
        name: "menuapp",
        components: { lacarte, ficheplat, macommande, commandeenvoyee },
        data: () => ({
            // Route path-based : /menu/:soc/:mag/:key/:table?/:r1?
            socParam: "", magParam: "", keyParam: "", tableParam: "",
            restaurantNom: "",
            items: [], loading: true, erreur: "",
            activeCat: "",

            // 'day' n'est plus jamais activ? : la carte s'affiche en entier,
            // sans onglet ? Menu du jour ? (la base ne porte de toute fa?on
            // aucun marqueur platdujour).
            tab: "full",
            search: "",

            /* -------- acc?s & catalogue (cf. bootstrap) -------- */
            bootState: "checking",        // checking | ready | offline | denied
            deviceId: "",
            sessionMeta: null,
            catalogueMeta: null,
            seedProgress: null,
            abortCtrl: null,
            demonte: false,
            view: "menu",                 // 'menu' | 'cart' | 'confirm'
            cart: [],
            platOuvert: null,
            clientNom: "",
            submitting: false,
            orderRef: "",
            lastOrder: { items: [], total: 0, count: 0 },
        }),
        computed: {
            tableLabel() {
                if (!this.tableParam || this.tableParam === "0") return "";
                return "Table " + this.tableParam;
            },
            categories() {
                const map = new Map();
                for (const it of this.items) {
                    const nom = (it.catalogue || "Autres").trim() || "Autres";
                    if (!map.has(nom)) map.set(nom, []);
                    map.get(nom).push(it);
                }
                return Array.from(map, ([nom, items]) => ({ nom, items }));
            },
            dayItems() { return this.items.filter(platDuJour); },
            hasDayMenu() { return this.dayItems.length > 0; },
            dateDuJour() {
                return new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
            },
            // Sections affichées : un seul rendu de carte pour les deux onglets.
            sections() {
                const base = this.tab === "day" && this.hasDayMenu
                    ? [{ nom: "Menu du jour", items: this.dayItems }]
                    : this.categories;
                const q = sansAccent(this.search.trim());
                if (!q) return base;
                return base
                    .map(cat => ({ nom: cat.nom, items: cat.items.filter(it => this.correspond(it, q)) }))
                    .filter(cat => cat.items.length);
            },
            cartCount() { return this.cart.reduce((s, l) => s + l.qte, 0); },
            cartTotal() { return this.cart.reduce((s, l) => s + l.unitPrice * l.qte, 0); },
            // Quantité déjà commandée, par article - la carte l'affiche sur la pastille.
            qtyParPid() {
                const m = {};
                for (const l of this.cart) m[l.pid] = (m[l.pid] || 0) + l.qte;
                return m;
            },
        },
        watch: {
            "$route.params.key": "loadMenu",
        },
        methods: {
            correspond(item, q) {
                const accomp = (item.accompagnement || []).map(a => a.name).join(" ");
                const champs = [item.libelle, item.catalogue, item.description, accomp].join(" ");
                return sansAccent(champs).indexOf(q) !== -1;
            },
            allerCategorie(nom) {
                this.activeCat = nom;
                const el = document.getElementById("lc-cat-" + slug(nom));
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            },
            // Scroll-spy : la barre de catégories suit la lecture, pas seulement les clics.
            onScroll() {
                if (this.view !== "menu" || this.tab !== "full" || this.search) return;
                let courant = "";
                for (const cat of this.categories) {
                    const el = document.getElementById("lc-cat-" + slug(cat.nom));
                    if (el && el.getBoundingClientRect().top <= 220) courant = cat.nom;
                }
                if (courant && courant !== this.activeCat) this.activeCat = courant;
            },

            ouvrirPlat(item) { this.platOuvert = item; },

            // Le prix vient de la fiche (portion + suppléments) : ce qui est affiché
            // au client est exactement ce qui est facturé.
            ajouterAuPanier(choix) {
                const accomp = choix.accompagnements.slice();
                const cle = l =>
                    [l.pid, l.tarifPid, (l.accompagnements || []).join("|"), l.note].join("§");
                const nouvelle = {
                    lineId: ++_uid,
                    pid: choix.item.pid,
                    libelle: choix.item.libelle,
                    image: choix.item.image || "",
                    tarifPid: choix.tarif.pid,
                    tarifLibelle: choix.tarif.libelle,
                    accompagnements: accomp,
                    note: choix.note,
                    unitPrice: choix.prixUnitaire,
                    qte: choix.qte,
                };
                // Même plat, même portion, mêmes accompagnements, même note → une seule ligne.
                const existante = this.cart.find(l => cle(l) === cle(nouvelle));
                if (existante) existante.qte += choix.qte;
                else this.cart.push(nouvelle);
                this.platOuvert = null;
            },
            changerQuantite(line, d) {
                line.qte += d;
                if (line.qte <= 0) this.supprimerLigne(line);
            },
            supprimerLigne(line) {
                this.cart = this.cart.filter(l => l.lineId !== line.lineId);
            },
            retourCarte() {
                this.view = "menu";
                window.scrollTo(0, 0);
            },

            // Envoi de la commande. TODO Partie C : POST vers l'endpoint public.
            async passerCommande() {
                if (!this.cart.length || this.submitting) return;
                this.submitting = true;
                const payload = {
                    soc: this.socParam, mag: this.magParam, key: this.keyParam,
                    table: this.tableParam, client: this.clientNom, total: this.cartTotal,
                    items: this.cart.map(l => ({
                        pid: l.pid, libelle: l.libelle,
                        tarif: l.tarifPid, tarifLibelle: l.tarifLibelle,
                        pu: l.unitPrice, qte: l.qte,
                        accompagnement: l.accompagnements.join(", "),
                        accompagnements: l.accompagnements,
                        note: l.note,
                    })),
                };
                try {
                    // --- Placeholder (à remplacer par l'appel backend Partie C) ---
                    // const url = `${process.env.VUE_APP_API_URL}/menu/commande/${payload.soc}/${payload.mag}/${payload.key}/${randomString()}`;
                    // const res = await axios.post(url, payload, { ... });
                    await new Promise(r => setTimeout(r, 600));
                    this.orderRef = "GT-" + Math.floor(100 + Math.random() * 900);
                    // ---------------------------------------------------------------
                    this.lastOrder = { items: [...this.cart], total: this.cartTotal, count: this.cartCount };
                    this.cart = [];
                    this.clientNom = "";
                    this.view = "confirm";
                    window.scrollTo(0, 0);
                } catch (e) {
                    if (window.toastr) window.toastr.error("Échec de l'envoi de la commande. Réessayez.");
                } finally {
                    this.submitting = false;
                }
            },

            /* ============================================================
               Démarrage - porté depuis panier/index.vue (bootstrap).
               ============================================================ */

            /**
             * Machine à états du démarrage.
             *
             * Le chemin rapide n'est pris que si les TROIS conditions tiennent :
             * session fraîche (borne la révocation à ttlSeconds), même clé qu'à
             * la dernière ouverture (détecte un QR régénéré), catalogue intègre
             * (détecte une écriture IndexedDB interrompue).
             */
            async bootstrap() {
                if (!this.socParam || !this.magParam || !this.keyParam) {


                    return this.refuser("PARAMS_INVALID");
                }

                
console.error("[sg] paramètres manquants", this.$route.params);

                this.bootState = "checking";
                this.loading = true;
                this.deviceId = await getOrCreateDeviceId();

                const session = await getSession(this.socParam, this.magParam);
                if (session && isSessionFresh(session) && session.key === this.keyParam) {
                    const meta = await getCatalogueMeta(this.socParam, this.magParam);
                    if (meta && await verifyCatalogueIntegrity(this.socParam, this.magParam, meta)) {
                        this.sessionMeta = session;
                        this.catalogueMeta = meta;
                        await this.demarrerMenu("ready");
                        // Aucun appel de contrôle ici, volontairement : tant que
                        // la session est fraîche, le serveur répondrait « rien
                        // n'a changé » à chaque ouverture, pour un aller-retour.
                        return;
                    }
                }

                console.log("[sg] bootstrap : session expirée ou catalogue absent / corrompu, vérification serveur…");

                await this.verifierEtCharger(false);
            },

            async verifierEtCharger(forcerFull) {
                const soc = this.socParam, mag = this.magParam;
                const meta = forcerFull ? null : await getCatalogueMeta(soc, mag);

                if (this.abortCtrl) this.abortCtrl.abort();
                this.abortCtrl = new AbortController();

                const res = await verifierAccesScanGo({
                    soc, mag,
                    key: this.keyParam,
                    deviceId: this.deviceId,
                    catalogueVersion: meta ? meta.version : null,
                    signal: this.abortCtrl.signal,
                });

                if (res.status === "denied") {
                    await this.purgerApresRefus(res.code);
                    return this.refuser(res.code);
                }

                if (res.status === "network") {
                    // Une panne réseau ne ferme jamais la carte à un client
                    // légitime : si on a de quoi l'afficher, on l'affiche.
                    const dispo = await getCatalogueMeta(soc, mag);
                    if (dispo) {
                        this.catalogueMeta = dispo;
                        await this.demarrerMenu("offline");
                        return;
                    }
                    return this.refuser("OFFLINE_NO_CACHE");
                }

                const ok = await this.appliquerReponse(res.data, forcerFull);
                if (ok) await this.demarrerMenu("ready");
            },

            /** Retourne false si un re-fetch complet a été relancé. */
            async appliquerReponse(data, dejaForce) {
                const soc = this.socParam, mag = this.magParam;
                const cat = data.catalogue || {};
                const meta = {
                    version: cat.version,
                    checksum: cat.checksum,
                    serverNow: data.serverNow,
                };

                if (cat.mode === "full") {
                    this.seedProgress = { fait: 0, total: (cat.upsert || []).length };
                    await saveCatalogue(soc, mag, cat.upsert || [], meta, (fait, total) => {
                        this.seedProgress = { fait, total };
                    });
                    this.seedProgress = null;
                } else if (cat.mode === "delta") {
                    await applyCatalogueDelta(soc, mag, cat, meta);
                } else {
                    // 'unchanged' : le serveur croit qu'on a déjà le catalogue.
                    const local = await getCatalogueMeta(soc, mag);
                    const sain = local && await verifyCatalogueIntegrity(soc, mag, local);
                    if (!sain && !dejaForce) {
                        // Un seul rattrapage, jamais de boucle.
                        await clearCatalogue(soc, mag);
                        await this.verifierEtCharger(true);
                        return false;
                    }
                    if (sain) await touchCatalogueMeta(soc, mag, data.serverNow);
                }

                await saveSession(soc, mag, {
                    key: this.keyParam,
                    serverNow: data.serverNow,
                    ttlSeconds: data.ttlSeconds,
                    quota: data.quota,
                    magLabel: data.magLabel,
                    socLabel: data.socLabel,
                });
                this.sessionMeta = await getSession(soc, mag);
                this.catalogueMeta = await getCatalogueMeta(soc, mag);
                return true;
            },

            /**
             * Le catalogue est en place : on le transforme en carte.
             *
             * Pas de purgeOtherScopes() ici, contrairement au panier : le menu
             * partage le scope (soc|mag) avec lui, et purger reviendrait à
             * effacer le catalogue ET le panier en cours du Scan & Go pour le
             * même magasin.
             */
            async demarrerMenu(etat) {
                try {
                    const articles = await getAllArticles(this.socParam, this.magParam);
                    this.items = catalogueVersCarte(articles);
                    this.erreur = this.items.length
                        ? ""
                        : "La carte de ce restaurant est vide pour le moment.";
                } catch (e) {
                    console.error("[sg] lecture du catalogue:", e);
                    this.items = [];
                    this.erreur = "Impossible d'afficher la carte.";
                }

                if (this.sessionMeta && this.sessionMeta.magLabel) {
                    this.restaurantNom = this.sessionMeta.magLabel;
                }

                this.activeCat = this.categories.length ? this.categories[0].nom : "";
                this.tab = "full";
                this.bootState = etat;
                this.loading = false;
            },

            /** Rechargement à la demande (bouton « recharger » de la carte). */
            async loadMenu() {
                this.erreur = "";
                await this.verifierEtCharger(false);
            },

            /* ---------------------------------------------------- refus */

            async purgerApresRefus(code) {
                const soc = this.socParam, mag = this.magParam;
                try {
                    await clearSession(soc, mag);
                    // Le catalogue est PARTAGÉ avec le Scan & Go : on ne le purge
                    // que si la clé ou le magasin sont en cause, jamais pour un
                    // refus temporaire (quota, rate-limit).
                    if (code === "KEY_INVALID" || code === "MAG_UNKNOWN") {
                        await clearCatalogue(soc, mag);
                    }
                } catch (e) {
                    console.error("[sg] purge après refus:", e);
                }
            },

            refuser(code) {
                if (this.demonte) return;
                this.bootState = "denied";
                this.loading = false;
                try {
                    // La clé fait 280 caractères et ne doit jamais transiter par
                    // une URL : la page de refus la relit ici pour « Réessayer ».
                    sessionStorage.setItem("sg_retry_url", this.$route.fullPath);
                } catch (e) { /* mode privé : on perd juste le bouton Réessayer */ }
                this.$router.replace({ name: "panierrefus", query: { c: code, t: "menu" } });
            },

            /* ------------------------------------------------ connexion */

            setOnline() {
                if (navigator.onLine && this.bootState === "offline") this.verifierEtCharger(false);
            },

            // Un onglet resté ouvert des heures : on ne revérifie que si la
            // session a expiré entre-temps.
            onVisibility() {
                if (document.visibilityState !== "visible") return;
                if (this.bootState === "denied" || this.bootState === "checking") return;
                if (!isSessionFresh(this.sessionMeta)) this.verifierEtCharger(false);
            },
        },
        async mounted() {
            // path: '/menu/:soc/:mag/:key/:table?/:r1?'
            const p = this.$route.params || {};
            //this.socParam = p.soc || "";
            this.socParam = p.key || "";
            this.magParam = p.mag || "";
            this.keyParam = p.key || "";
            this.tableParam = p.table || "";

            window.addEventListener("scroll", this.onScroll, { passive: true });
            window.addEventListener("online", this.setOnline);
            window.addEventListener("offline", this.setOnline);
            document.addEventListener("visibilitychange", this.onVisibility);

            await this.bootstrap();
        },
        beforeUnmount() {
            // EN PREMIER : les callbacks async encore en vol ne doivent plus
            // naviguer une fois le composant démonté (refuser() teste ce drapeau).
            this.demonte = true;

            document.body.style.overflow = "";
            window.removeEventListener("scroll", this.onScroll);
            window.removeEventListener("online", this.setOnline);
            window.removeEventListener("offline", this.setOnline);
            document.removeEventListener("visibilitychange", this.onVisibility);
            if (this.abortCtrl) this.abortCtrl.abort();
        },
    };
</script>

<style scoped>
    /* eslint-disable */
    @import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap");

    /* ──────────────────────────────────────────────────────────────────────
       Thème « Gourmandise Expressive » (export Stitch, stitch_menu_qr_express).
       Surfaces chaudes rosées + accent rouge unique. Les jetons sont déclarés
       ici et hérités par lacarte / ficheplat / macommande / commandeenvoyee.
       Écran mobile uniquement : la mise en page est bornée à 480px et centrée.
       ────────────────────────────────────────────────────────────────────── */
    .mnu-app {
        /* Couleurs */
        --c-primary: #FF3008;
        --c-primary-hover: #E02900;
        --c-bg: #FFFFFF;          /* fond des écrans en blanc franc */
        --c-surface: #FFFFFF;
        --c-surface-low: #FFF0EE;
        --c-surface-container: #FFE9E5;
        --c-surface-high: #FFE2DC;
        --c-on-surface: #2A1713;
        --c-on-surface-variant: #5E3F39;
        --c-outline: #936E67;
        --c-outline-variant: #E9BCB4;
        --c-inverse-surface: #412B27;
        --c-accent-warm: #FFC93C;
        --c-success: #1BAA5C;
        --c-error: #BA1A1A;
        --c-error-container: #FFDAD6;

        /* Rythme (échelle 4px du design system) */
        --sp-xs: 8px;
        --sp-sm: 12px;
        --sp-md: 16px;
        --sp-lg: 24px;
        --sp-xl: 32px;
        --sp-pad: 20px;

        max-width: 480px;
        margin: 0 auto;
        min-height: 100vh;
        background: var(--c-bg);
        color: var(--c-on-surface);
        font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    .mnu-footer {
        text-align: center; padding: var(--sp-lg) var(--sp-pad) 132px;
        font-size: 12px; color: var(--c-outline);
    }

    /* Écran de vérification d'accès, avant tout affichage de la carte. */
    .mnu-boot {
        min-height: 100vh;
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: var(--sp-sm); padding: var(--sp-pad);
    }
    .mnu-boot-spin {
        width: 38px; height: 38px; border-radius: 50%;
        border: 3px solid var(--c-surface-high);
        border-top-color: var(--c-primary);
        animation: mnu-spin .7s linear infinite;
    }
    @keyframes mnu-spin { to { transform: rotate(360deg); } }

    @media (prefers-reduced-motion: reduce) {
        .mnu-boot-spin { animation-duration: 2.4s; }
    }

    .mnu-boot-txt {
        margin: 0; font-size: 15px; font-weight: 600;
        color: var(--c-on-surface-variant);
    }
    .mnu-boot-sub {
        margin: 0; font-size: 13px; color: var(--c-outline);
        font-variant-numeric: tabular-nums;
    }

    /* Bandeau hors ligne : la carte affichée vient du cache de l'appareil. */
    .mnu-offline {
        position: sticky; top: 0; z-index: 30;
        display: flex; align-items: center; gap: var(--sp-xs);
        padding: 10px var(--sp-pad);
        background: var(--c-accent-warm);
        color: #3A2A00;
        font-size: 13px; font-weight: 600;
    }
</style>
